import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout';

/**
 * UP-FRONTEND-PERF-OPT-1 (N51): route-level code-splitting.
 * Все страницы грузятся через `React.lazy()` — каждая попадает в свой
 * чанк и не тянется в main entry bundle. Это главный рычаг для цели
 * «entry chunk < 200KB gzip»: тяжёлые страницы (Memory с cognitive-map,
 * Conversations с reasoning-панелями) больше не входят в стартовую
 * загрузку.
 *
 * Поведение Login/PrivacyPolicy/Terms/Consent сохранено — меняется только
 * способ загрузки модуля (динамический import вместо статического).
 */
const LoginPage = lazy(() => import('@/pages/Login').then((m) => ({ default: m.LoginPage })));
const DashboardPage = lazy(() =>
  import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })),
);
const MemoryPage = lazy(() => import('@/pages/Memory').then((m) => ({ default: m.MemoryPage })));
const AgentsPage = lazy(() => import('@/pages/Agents').then((m) => ({ default: m.AgentsPage })));
const TasksPage = lazy(() => import('@/pages/Tasks').then((m) => ({ default: m.TasksPage })));
const ApprovalsPage = lazy(() =>
  import('@/pages/Approvals').then((m) => ({ default: m.ApprovalsPage })),
);
const PrivacyPage = lazy(() =>
  import('@/pages/Privacy').then((m) => ({ default: m.PrivacyPage })),
);
const RemindersPage = lazy(() =>
  import('@/pages/Reminders').then((m) => ({ default: m.RemindersPage })),
);
const ConversationsPage = lazy(() =>
  import('@/pages/Conversations').then((m) => ({ default: m.ConversationsPage })),
);
const IntegrationsPage = lazy(() =>
  import('@/pages/Integrations').then((m) => ({ default: m.IntegrationsPage })),
);
const McpPage = lazy(() => import('@/pages/Mcp').then((m) => ({ default: m.McpPage })));
const ScheduledTasksPage = lazy(() =>
  import('@/pages/ScheduledTasks').then((m) => ({ default: m.ScheduledTasksPage })),
);
const CalendarPage = lazy(() =>
  import('@/pages/Calendar').then((m) => ({ default: m.CalendarPage })),
);
const ListsPage = lazy(() => import('@/pages/Lists').then((m) => ({ default: m.ListsPage })));
const ProfilePage = lazy(() =>
  import('@/pages/Profile').then((m) => ({ default: m.ProfilePage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/Settings').then((m) => ({ default: m.SettingsPage })),
);
const SubscriptionPage = lazy(() =>
  import('@/pages/Subscription').then((m) => ({ default: m.SubscriptionPage })),
);
const BillingSuccessPage = lazy(() =>
  import('@/pages/BillingSuccess').then((m) => ({ default: m.BillingSuccessPage })),
);
const PrivacyConsentPage = lazy(() =>
  import('@/pages/PrivacyConsent').then((m) => ({ default: m.PrivacyConsentPage })),
);
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/legal/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicyPage })),
);
const TermsPage = lazy(() => import('@/pages/legal/Terms').then((m) => ({ default: m.TermsPage })));
const ConsentPage = lazy(() =>
  import('@/pages/legal/Consent').then((m) => ({ default: m.ConsentPage })),
);

/**
 * Лёгкий fallback для Suspense — нейтральный спиннер на полную высоту
 * контентной области. Совпадает по стилю с auth-loader в Layout.
 */
function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-24" role="status" aria-live="polite">
      <div className="w-10 h-10 rounded-full border-4 border-primary-200 border-t-primary-500 animate-spin" />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}

/**
 * Главный компонент приложения
 * @contract Роутинг и структура приложения
 */
export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Публичные роуты */}
          <Route path="/login" element={<LoginPage />} />

          {/* UP-15 (2026-05-17): legal-страницы доступны без auth для
               пользователей, которые ещё не вошли в систему. */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/consent" element={<ConsentPage />} />

          {/* Защищённые роуты */}
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/memory" element={<MemoryPage />} />
            {/* PROFILE-CARD-1 (2026-05-16): «Профиль глазами агента» */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            {/* Day 1 (2026-05-14): «Почему агент так ответил?» */}
            <Route path="/conversations" element={<ConversationsPage />} />
            <Route path="/reminders" element={<RemindersPage />} />
            {/* CAL-EMBED-1 Phase 2: embedded calendar (read-only) */}
            <Route path="/calendar" element={<CalendarPage />} />
            {/* UP-29 / S1 Phase C (2026-05-26): Smart Lists section */}
            <Route path="/lists" element={<ListsPage />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            {/* S13.1 (2026-05-27): dedicated MCP catalog page */}
            <Route path="/mcp" element={<McpPage />} />
            {/* NBF-SCHED MVP SCHED-7b (2026-05-30): запланированные задачи */}
            <Route path="/scheduled-tasks" element={<ScheduledTasksPage />} />
            {/* UP-14 Phase 1 PR-3 (2026-05-20): Settings */}
            <Route path="/settings" element={<SettingsPage />} />
            {/* N16 Phase 2 step 2 (2026-06-07): pricing + checkout. */}
            <Route path="/subscription" element={<SubscriptionPage />} />
            {/* N16 Phase 2 step 3 (2026-06-07): YooKassa return-page + polling. */}
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            {/* UP-23b Phase B3 (2026-05-20): 152-ФЗ consent flow */}
            <Route path="/privacy-consent" element={<PrivacyConsentPage />} />
          </Route>

          {/* Редиректы */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
